import {
  Alert,
  Box,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { parseInt } from "lodash";
import { useEffect, useState } from "react";
import axios from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import {
  addDaysToDate,
  GetDateInYYYYMMDDWithoutTimeFormat,
  getValueInCurrency,
  isInitialDateGreaterThanFinalDate,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import EventResolutions from "./EventResolutions";
import ErrorMessage from "../../../components/common/ErrorMessage";
import AdditionalData from "./AdditionalData";
import {
  getCurrencyFromSetup,
  getUserData,
} from "../../../common/EntitiesManagement";
import ClientBranchSelector from "./ClientBranchSelector";
import ExpandableView from "../../../components/common/ExpandableView";
import { Add, Cancel } from "@mui/icons-material";
import CreatedByText from "../../../components/common/CreatedByText";
import AddressManagement from "../../../components/common/AddressManagement";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";

const AddEvent = () => {
  const {
    // endpoints
    PERSONAL_API,
    CLIENTS_API,
    EVENTTYPE_API,
    VEHICLE_ENDPOINT,
    PROCEEDING_ENDPOINT,
    CONTRACTOR_API,
    // Models
    eventModel,

    // Global States Modifiers
    setEventModel,
    getClientTypes,
    config,
  } = useGlobalContext();

  const [clientTypes, setClientTypes] = useState(null);
  const [contractorsData, setContractorsData] = useState(null);
  const [eventTypeData, setEventTypeData] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [personalDataFiltered, setPersonalDataFiltered] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [proceedingData, setProceedingData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [clientBranches, setClientBranches] = useState([]);
  const [showClientBranchesModal, setShowClientBranchesModal] = useState(false);
  const [personalAssignmentMode, setPersonalAssignmentMode] = useState("free");
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [descriptionCount, setDescriptionCount] = useState(
    eventModel?.Observation?.length ?? 0,
  );
  const [showAddClient, setShowAddClient] = useState(false);

  const getclientTypesData = async () => {
    const clientT = await getClientTypes();
    setClientTypes(clientT);
  };

  const getContractorsData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CONTRACTOR_API}`, requestConfig);
    setContractorsData(data?.data?.Data);
  };

  const getClientBranches = async (clientId) => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${CLIENTS_API}/${clientId}/branch`,
      requestConfig,
    );

    setClientBranches(data?.data?.Data);
  };

  const getProceedingData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PROCEEDING_ENDPOINT}`, requestConfig);

    setProceedingData(data?.data);
  };

  const getVehicleData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${VEHICLE_ENDPOINT}/assigned`, requestConfig);

    setVehicleData(data?.data);
  };

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}`, requestConfig);

    setPersonalData(data?.data);
    setPersonalDataFiltered(data?.data?.Data);
  };

  const getClients = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CLIENTS_API}`, requestConfig);

    setClientsData(data?.data);
  };

  const getEventTypes = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTTYPE_API}`, requestConfig);

    setEventTypeData(data?.data);
  };

  useEffect(() => {
    getPersonal();
    getClients();
    getEventTypes();
    getVehicleData();
    getProceedingData();
    getclientTypesData();
    getContractorsData();
  }, []);

  const handleClientTypeChange = (selectedClientType) => {
    if (!selectedClientType) {
      setEventModel({
        ...eventModel,
        clientModel: {
          ...eventModel?.clientModel,
          clientTypeId: 0,
          ClientType: null,
        },
      });
      return;
    }

    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        clientTypeId: selectedClientType.Id,
        ClientType: selectedClientType,
      },
    });
  };

  useEffect(() => {
    if (clientBranches && clientBranches.length > 0) {
      setShowClientBranchesModal(true);
    }
  }, [clientBranches]);

  const handleChange = (e) => {
    if (e.target.name === "Observation") {
      setDescriptionCount(e.target.value.length);
    }

    setEventModel({ ...eventModel, [e.target.name]: e.target.value });
  };

  const handleExecutionValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        executionValue: 0,
        executionFinalValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      executionValue: inputValue > 0 ? inputValue : 0,
      executionFinalValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleSparePartsValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        sparePartsValue: 0,
        sparePartsFinalValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      sparePartsValue: inputValue > 0 ? inputValue : 0,
      sparePartsFinalValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleSparePartsRealValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        sparePartsRealValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      sparePartsRealValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleEventTypeChange = (currentEventType) => {
    if (!currentEventType) {
      setEventModel({
        ...eventModel,
        EventTypeId: 0,
        EventType: undefined,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      EventTypeId: currentEventType.Id,
      EventType: currentEventType,
      executionValue: currentEventType.executionValue,
      executionFinalValue: currentEventType.executionValue,
      ExpectedSolutionFinalDateValue: addDaysToDate(
        eventModel?.ExpectedSolutionDate,
        currentEventType?.AttentionDays ?? 0,
      ),
      ExpectedSolutionFinalDate: GetDateInYYYYMMDDWithoutTimeFormat(
        addDaysToDate(
          eventModel?.ExpectedSolutionDate,
          currentEventType?.AttentionDays ?? 0,
        ),
      ),
    });
  };

  const handlePersonalAssignedChange = (currentPersonal) => {
    if (!currentPersonal) {
      setEventModel({
        ...eventModel,
        AssignedBy: null,
        PersonalAssigned: null,
        PersonalId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      AssignedBy: getUserData()?.UserName,
      PersonalAssigned: currentPersonal,
      PersonalId: currentPersonal.id,
    });
  };

  const handleVehicleChange = (currentVehicle) => {
    if (!currentVehicle) {
      setEventModel({
        ...eventModel,
        VehicleAssigned: null,
        VehicleId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      VehicleAssigned: currentVehicle,
      VehicleId: currentVehicle.Id,
    });
  };

  const handleClientChange = async (currentClient) => {
    if (currentClient) {
      setEventModel({
        ...eventModel,
        ClientId: currentClient.Id,
        Client: currentClient,
        Address: currentClient.Address,
        City: currentClient.City,
        State: currentClient.State,
        StateId: currentClient.State.Id,
        CityId: currentClient.City.Id,
        ReporterName: currentClient.ContactName
          ? currentClient.ContactName
          : currentClient.Name,
        ReporterEmail: currentClient.Email,
        ReporterPhoneNumber: currentClient.PhoneNumber,
        AddressComplement: currentClient.AddressComplement,
        ClientBranchId: 0,
      });

      getClientBranches(currentClient.Id);
    } else {
      setEventModel({
        ...eventModel,
        ClientId: 0,
        Client: null,
        ClientBranchId: 0,
      });
    }
  };

  const validateExpectedSolutionDates = (initial, final) => {
    if (isInitialDateGreaterThanFinalDate(initial, final)) {
      setErrorMessage(localize.date_range_error);
      setShowErrorModal(true);
      return false;
    }

    return true;
  };

  const handleExpectedAttentionDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: null,
          ExpectedSolutionDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          expectedSolutionDate,
          eventModel.ExpectedSolutionFinalDateValue,
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: expectedSolutionDate,
          ExpectedSolutionDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const handleExpectedAttentionFinalDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: null,
          ExpectedSolutionFinalDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          eventModel.ExpectedSolutionDateValue,
          expectedSolutionDate,
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: expectedSolutionDate,
          ExpectedSolutionFinalDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const handleProceedingChange = (e) => {
    if (!e) {
      setEventModel({
        ...eventModel,
        ProceedingInfo: null,
        ProceedingId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      ProceedingInfo: e,
      ProceedingId: e.Id,
    });
  };

  const getBoxStyle = () => "w-full flex justify-between gap-2 items-center";

  const handleClientIdentificationTypeChange = (e) => {
    if (!e) {
      setEventModel({
        ...eventModel,
        clientModel: {
          ...eventModel?.clientModel,
          IdentificationTypeId: "Cédula de ciudadanía",
          IdentificationType: {
            Id: "Cédula de ciudadanía",
            Name: "Cédula de ciudadanía",
          },
        },
      });
      return;
    }

    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        IdentificationTypeId: e.Id,
        IdentificationType: { ...e },
      },
    });
  };

  const handleClientIdentificationChange = (e) => {
    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        Identification: e.target.value,
      },
    });
  };

  const handleContractorChange = (currentContractor) => {
    if (!currentContractor) {
      setEventModel({
        ...eventModel,
        Contractor: undefined,
        ContractorId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      Contractor: currentContractor,
      ContractorId: currentContractor.Id,
    });
  };

  const renderGroupHeader = (title) => (
    <Box className="w-full flex flex-col gap-1 mt-2">
      <Typography
        className="main_text_color"
        style={{ fontWeight: 600, fontSize: "0.95rem" }}
      >
        {title}
      </Typography>
      <Divider />
    </Box>
  );

  const getIncomeColor = (supplyValue, realValue) => {
    const income = supplyValue - realValue;

    if (income < 0) {
      return "crimson";
    }

    if (income > 0) {
      return "green";
    }

    return "black";
  };

  const handleAssignmentModeChange = (e) => {
    setPersonalAssignmentMode(e.target.value);
  };

  useEffect(() => {
    if (!eventModel?.Zone) {
      setPersonalAssignmentMode("free");
    }
  }, [eventModel?.Zone]);

  useEffect(() => {
    if (personalAssignmentMode === "zone") {
      setEventModel({
        ...eventModel,
        AssignedBy: null,
        PersonalAssigned: null,
        PersonalId: 0,
      });
    }

    if (personalData?.Data) {
      const personalFiltered =
        personalAssignmentMode === "zone"
          ? personalData.Data.filter(
              (item) =>
                !!item.Zones?.find((zone) => zone.Id === eventModel?.ZoneId),
            )
          : [...personalData.Data];

      setPersonalDataFiltered(personalFiltered);
    }
  }, [personalAssignmentMode]);

  const getComponents = () => {
    const result = [
      {
        Name: localize.general_information,
        Component: (
          <Box className="w-full flex flex-col gap-3 mt-2">
            {renderGroupHeader(localize.service_order)}
            <Box className="flex gap-2 items-center">
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.Code}
                onChange={handleChange}
                size="small"
                fullWidth
                id="Code"
                label={localize.code}
                name="Code"
                variant="outlined"
                autoComplete="off"
              />

              {eventModel?.Id <= 0 && (
                <Alert className="w-full" severity="info">
                  <label className="text-xs">
                    {localize.info_code_creation}
                  </label>
                </Alert>
              )}
            </Box>

            {contractorsData?.length > 0 && (
              <Combobox
                id="Contractor"
                handleValueChange={handleContractorChange}
                fieldValue={eventModel?.Contractor}
                fieldOptions={contractorsData ?? []}
                popperFullWidth={PopperFullWidth}
                fieldLabel={localize.contractor}
                customOptionLabel={(option) =>
                  `${option.Identification} - ${option.Name}`
                }
              />
            )}

            {proceedingData?.Data?.length > 0 && (
              <Combobox
                id="Proceeding"
                fieldLabel={localize.proceeding}
                fieldOptions={proceedingData?.Data ?? []}
                fieldValue={eventModel?.ProceedingInfo}
                handleValueChange={handleProceedingChange}
                popperFullWidth={PopperFullWidth}
                customOptionLabel={(option) =>
                  `${option?.Name ?? ""} ${
                    option?.Code ? `${localize.code}: (${option.Code})` : ""
                  }`
                }
              />
            )}

            {eventModel?.ProceedingInfo?.Description && (
              <Alert severity="info" className="w-full">
                <span className="text-xs">
                  {eventModel.ProceedingInfo.Description}
                </span>
              </Alert>
            )}

            <Combobox
              isRequired
              id="EventTypeId"
              fieldLabel={localize.event_type}
              fieldOptions={eventTypeData?.Data ?? []}
              fieldValue={eventModel?.EventType}
              handleValueChange={handleEventTypeChange}
              popperFullWidth={PopperFullWidth}
            />

            {eventModel?.EventType?.Description && (
              <Alert severity="info" className="w-full">
                <span className="text-xs">
                  {eventModel.EventType.Description}
                </span>
              </Alert>
            )}

            {renderGroupHeader(localize.client_information)}
            <Box className="flex gap-2 w-full items-center">
              <Combobox
                disabled={showAddClient}
                id="Client"
                fieldLabel={localize.client}
                fieldOptions={clientsData?.Data ?? []}
                fieldValue={eventModel?.Client}
                handleValueChange={handleClientChange}
                popperFullWidth={PopperFullWidth}
                customOptionLabel={(option) =>
                  `${option.Identification} - ${option.Name}`
                }
              />

              {(!eventModel?.Id || eventModel?.Id === 0) && (
                <Tooltip
                  title={
                    !showAddClient
                      ? localize.add_client
                      : localize.cancel_client_creation
                  }
                >
                  <button
                    onClick={
                      !showAddClient
                        ? () => {
                            setEventModel({
                              ...eventModel,
                              ClientId: 0,
                              Client: null,
                              ClientBranchId: 0,
                            });
                            setShowAddClient(true);
                          }
                        : () => {
                            setEventModel({
                              ...eventModel,
                              clientModel: {},
                            });
                            setShowAddClient(false);
                          }
                    }
                  >
                    {showAddClient ? (
                      <Cancel
                        sx={{ color: "red", width: "24px", height: "24px" }}
                      />
                    ) : (
                      <Add
                        sx={{ color: "green", width: "24px", height: "24px" }}
                      />
                    )}
                  </button>
                </Tooltip>
              )}
            </Box>

            {showAddClient && (
              <Alert severity="info" className="w-full">
                <span className="text-xs">{localize.new_client_message}</span>
              </Alert>
            )}

            {showAddClient && (
              <Combobox
                isRequired
                id="ClientType"
                handleValueChange={handleClientTypeChange}
                fieldValue={eventModel?.clientModel?.ClientType}
                fieldOptions={clientTypes ?? []}
                fieldLabel={localize.client_type}
              />
            )}

            {showAddClient && (
              <Box className={getBoxStyle()}>
                <Combobox
                  isRequired
                  id="IdentificationType"
                  handleValueChange={handleClientIdentificationTypeChange}
                  fieldValue={
                    eventModel?.clientModel?.IdentificationType ?? null
                  }
                  fieldOptions={[
                    {
                      Id: "Cédula de ciudadanía",
                      Name: "Cédula de ciudadanía",
                    },
                    { Id: "Nit", Name: "Nit" },
                    { Id: "Pasaporte", Name: "Pasaporte" },
                    {
                      Id: "Cédula de extranjería",
                      Name: "Cédula de extranjería",
                    },
                    { Id: "Otro", Name: "Otro" },
                  ]}
                  fieldLabel={localize.identification_type}
                />

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Identification"
                  value={eventModel?.clientModel?.Identification}
                  id="Identification"
                  label={localize.identification}
                  variant="outlined"
                  onChange={handleClientIdentificationChange}
                  autoComplete="off"
                />
              </Box>
            )}

            {showAddClient && (
              <TextField
                required
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                size="small"
                id="ReporterName"
                name="ReporterName"
                value={eventModel.ReporterName}
                onChange={handleChange}
                label={localize.client_name}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            )}

            <AddressManagement model={eventModel} setModel={setEventModel} />

            <Box className={getBoxStyle()}>
              <TextField
                required
                size="small"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.ReporterPhoneNumber}
                onChange={handleChange}
                fullWidth
                id="ReporterPhoneNumber"
                name="ReporterPhoneNumber"
                label={
                  showAddClient
                    ? localize.phone_number
                    : localize.reporter_phone_number
                }
                variant="outlined"
                autoComplete="off"
              />

              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.ReporterEmail}
                onChange={handleChange}
                size="small"
                id="ReporterEmail"
                name="ReporterEmail"
                label={showAddClient ? localize.email : localize.reporter_email}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            </Box>

            {!showAddClient && (
              <TextField
                required
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                size="small"
                id="ReporterName"
                name="ReporterName"
                value={eventModel.ReporterName}
                onChange={handleChange}
                label={localize.reported_by}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            )}

            {renderGroupHeader(localize.expected_attention_date)}
            <Box className={getBoxStyle()}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={`${localize.start}`}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperProps={{ style: { zIndex: 1500 } }}
                  value={eventModel?.ExpectedSolutionDateValue}
                  onChange={handleExpectedAttentionDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: { fontSize: "1em", paddingBottom: "10px" },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={`${localize.end}`}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperProps={{ style: { zIndex: 1500 } }}
                  value={eventModel?.ExpectedSolutionFinalDateValue}
                  onChange={handleExpectedAttentionFinalDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: {
                          fontSize: "1em",
                          paddingBottom: "10px",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            {renderGroupHeader(localize.assignment)}
            {eventModel?.Zone && (
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby={`$zone-assignment-rbtn-label`}
                  defaultValue="free"
                  name="radio-buttons-group"
                  onChange={handleAssignmentModeChange}
                >
                  <FormControlLabel
                    value="free"
                    control={<Radio />}
                    label={
                      <span className="text-xs">
                        <PageTitleSubtitle
                          useEventTitleStyle
                          useFieldTitleStyle
                          title={localize.free_assignment}
                          subtitle={localize.free_assignment_description}
                        />
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="zone"
                    control={<Radio />}
                    label={
                      <span className="text-xs">
                        <PageTitleSubtitle
                          useEventTitleStyle
                          useFieldTitleStyle
                          title={localize.zone_assignment}
                          subtitle={localize.zone_assignment_description}
                        />
                      </span>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}

            <Combobox
              id="PersonalAssigned"
              fieldLabel={localize.assigned_to}
              fieldOptions={personalDataFiltered ?? []}
              fieldValue={eventModel?.PersonalAssigned}
              customOptionLabel={(option) =>
                `${option.Identification} - ${option.SurName} ${option.LastName}`
              }
              handleValueChange={handlePersonalAssignedChange}
              popperFullWidth={PopperFullWidth}
            />

            {vehicleData?.Data?.length > 0 && (
              <Combobox
                id="vehicleAssigned"
                fieldLabel={localize.vehicle}
                fieldOptions={vehicleData?.Data ?? []}
                fieldValue={eventModel?.VehicleAssigned}
                customOptionLabel={(option) =>
                  `(${option.VehicleType.Name}) ${option.Identification} - ${option.AssignedTo.SurName} ${option.AssignedTo.LastName}`
                }
                handleValueChange={handleVehicleChange}
                popperFullWidth={PopperFullWidth}
              />
            )}

            {renderGroupHeader(localize.service_values)}
            <Box className={getBoxStyle()}>
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.executionFinalValue}
                onChange={handleExecutionValue}
                size="small"
                id="executionFinalValue"
                name="executionFinalValue"
                label={localize.service_value}
                variant="outlined"
                fullWidth
              />

              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.sparePartsFinalValue}
                onChange={handleSparePartsValue}
                size="small"
                id="sparePartsFinalValue"
                name="sparePartsFinalValue"
                label={localize.spare_parts_value}
                variant="outlined"
                fullWidth
              />
            </Box>

            <Box className={getBoxStyle()}>
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.sparePartsRealValue}
                onChange={handleSparePartsRealValue}
                size="small"
                id="sparePartsRealValue"
                name="sparePartsRealValue"
                label={localize.spare_parts_real_value}
                variant="outlined"
                fullWidth
              />

              <Box className="flex flex-col w-full">
                <Typography>
                  <span className="text-xs font-bold">
                    {localize.income_by_supply}:
                  </span>
                </Typography>

                <Typography
                  sx={{
                    color: `${getIncomeColor(
                      eventModel.sparePartsFinalValue,
                      eventModel.sparePartsRealValue,
                    )}`,
                  }}
                >
                  <span className="text-xs">
                    {eventModel.sparePartsRealValue > 0 &&
                    eventModel.sparePartsFinalValue > 0
                      ? getValueInCurrency(
                          getCurrencyFromSetup(),
                          eventModel.sparePartsFinalValue -
                            eventModel.sparePartsRealValue,
                        )
                      : getValueInCurrency(getCurrencyFromSetup(), 0)}
                  </span>
                </Typography>
              </Box>
            </Box>

            <Box className={getBoxStyle()}>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={{
                  ...getInputProps(),
                  maxLength: maximumCharactersForDescription,
                }}
                size="small"
                fullWidth
                multiline
                rows={4}
                id="Observation"
                name="Observation"
                label={localize.observations}
                variant="outlined"
                onChange={handleChange}
                value={eventModel.Observation}
                helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                autoComplete="off"
              />
            </Box>
          </Box>
        ),
      },
    ];

    if (
      eventModel?.EventType?.AdditionalFields &&
      eventModel?.EventType?.AdditionalFields.length > 2
    ) {
      result.push({
        Name: localize.additional_data,
        Component: (
          <div className="w-full flex justify-center content-center items-center">
            <AdditionalData event={eventModel} setEvent={setEventModel} />
          </div>
        ),
      });
    }

    result.push({
      Name: localize.resolutions,
      Component: (
        <div className="w-full flex justify-center content-center items-center">
          <EventResolutions />
        </div>
      ),
    });

    return result;
  };

  return (
    <Container
      style={{ width: "100%", paddingLeft: "0px", paddingRight: "0px" }}
    >
      <div className="w-full">
        {showErrorModal && (
          <ErrorMessage
            message={errorMessage}
            title={localize.error}
            showModal={showErrorModal}
            setShowModal={setShowErrorModal}
          />
        )}

        {showClientBranchesModal && (
          <ClientBranchSelector
            show={showClientBranchesModal}
            setShow={setShowClientBranchesModal}
            eventModel={eventModel}
            setEventModel={setEventModel}
            client={eventModel?.Client}
            branches={clientBranches}
          />
        )}

        <ExpandableView components={getComponents()} />

        <CreatedByText entity={eventModel} />
      </div>
    </Container>
  );
};

export default AddEvent;
